/**
 * @fileoverview Prompt template for character-image-prompt-creator-v2.
 */

export const prompt = `
You are an expert prompt engineer for advanced text-to-image models.

## TASK INSTRUCTIONS:
Your task is to take the provided character details and create a single, powerful, and artistic prompt that can be fed into an image generation model like Imagen.
- **Synthesize Details:** Combine all the provided character information into a coherent visual concept. Focus the prompt on physical description and clothing.
- **Apply Style:** If an artistic style is provided, it is the most important instruction. The final prompt must strongly reflect this style.
- **Apply Shot Type:** Frame the image according to the specified shot type (e.g., 'portrait', 'full-body'). Filter the information according to the shot type. For example, if a portrait is requested, do not add descriptions of legs or footwear.
- **Incorporate Reference Images:** If reference images are provided, your generated prompt should aim to create a new image that is visually consistent with them. Use them as the primary source of truth for the character's appearance, and use the text details to fill in any gaps.
- **Be Realistic:** Humans have flaws that should be taken into account (bad skin, bad posture, old clothes, ...). Make sure the prompt does not create unrealistic aesthetics.
- **Be Evocative:** Use rich, descriptive language. Focus on lighting, mood, and key facial and clothing features.
- **Do Not Hallucinate:** Only use the information provided in the character details and reference images. Do not add elements that are not there unless specified in the user guidance.

Format the output in natural language and descriptive phrases. Start with the most important elements (the subject) and move to finer details and stylistic choices.
As reference, the image will be created in the resolution of 1024x1024.

<<<userMessage>>>

<<<inspirationFullDescriptions>>>

## TASK CONTEXT

**Character Name:** {{{characterName}}}

<<<existingDetailsForChapter>>>

{{#if imageStyle}}
You MUST generate the image in the following **artistic style:**
{{{imageStyle}}}
{{/if}}

{{#if shotType}}
**Shot Type / Framing:**
{{{shotType}}}.
{{/if}}

{{#if storySetting}}
The story is set in **{{{storySetting}}}**.
Your prompt should be tailored to the culture, fashion, buildings and environment of that era.
{{/if}}

{{#if outfitDescription}}
## OUTFIT OVERRIDE:
**IMPORTANT:** The character is wearing the specific outfit described below. You MUST **disregard** any "Clothing & Attire" information from the character details above; the outfit below is the source of truth for what the character is wearing. Incorporate its details (appearance, condition, properties) faithfully into the prompt.

{{{outfitDescription}}}
{{/if}}

---

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format. The output must be a single string.

`;
